import arrayMutators from "final-form-arrays";
import { FC, useEffect } from "react";
import { Form, FormRenderProps } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import Role from "constants/role";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isEmail } from "helpers/validate";
import { usePaymentFailure } from "hooks/use-payment-failure";
import { IAgent, selectAgentsAlphabetically } from "store/entities/agents";
import { selectDefaultMailboxUsed } from "store/entities/license";
import { selectIsUpdatingSubscription } from "store/entities/subscriptions";
import { WelcomeTutorialViewActions, getIsInvitingAgents, getPartialInvite } from "store/views/welcome-tutorial";
import { ISimpleAgentFormRow, containsValidEmails } from "utils/agent";
import { plural } from "utils/string";

import { InviteForm } from "../components/InviteForm";
import StepScreen from "../components/StepScreen";
import { WelcomeTutorialRoutes, WelcomeTutorialStep } from "../helpers";

interface IValidatedInvitedAgent {
  email: string;
  permission: Role;
}
interface IFormValues {
  agents: ISimpleAgentFormRow[];
}

const isValidAgent = (agent: ISimpleAgentFormRow): agent is IValidatedInvitedAgent =>
  Boolean(agent.email && isEmail(agent.email) && agent.permission);

export const InviteTeammates: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isInviting = useSelector(getIsInvitingAgents);
  const isSaving = useSelector(selectIsUpdatingSubscription);
  const agents = useSelector(selectAgentsAlphabetically);
  const defaultInboxUsed = useSelector(selectDefaultMailboxUsed);
  const { failedAgents, exceededBy } = useSelector(getPartialInvite);
  const { failed, reset, error } = usePaymentFailure();

  useEffect(() => {
    trackEngagement("Welcome tutorial: Invite Your Team section opened", EventPlace.WelcomeTutorial);
  }, []);

  const onSubmit = ({ agents }: IFormValues): void => {
    reset();
    const validAgents = agents.filter(isValidAgent).map(agent => ({
      email: agent.email,
      permission: agent.permission,
    }));

    if (validAgents.length) {
      dispatch(WelcomeTutorialViewActions.inviteAgents(validAgents));
      trackEngagement("Welcome tutorial: Agents added", EventPlace.WelcomeTutorial, {
        numberOfAgentsInvited: validAgents.length,
      });
    }
  };

  const onSkip = (): void => {
    trackEngagement("Welcome tutorial: Skip inviting agents", EventPlace.WelcomeTutorial);
    history.push(WelcomeTutorialRoutes.FORWARDING);
    if (defaultInboxUsed) {
      trackEngagement("Welcome tutorial: Finished", EventPlace.WelcomeTutorial, {
        sawForwardingStep: false,
      });
      dispatch(WelcomeTutorialViewActions.finish());
    }
  };

  const onBack = (): void => {
    trackEngagement("Welcome tutorial: Back from inviting agents", EventPlace.WelcomeTutorial);
    history.push(WelcomeTutorialRoutes.MAILBOX);
  };

  const shouldShowSkip = (
    containsValidEmails: boolean,
    touchedFields: { [key: string]: boolean } | undefined,
    failed: boolean
  ): boolean => {
    const werePermissionsTouched = Object.entries(touchedFields ?? {}).some(
      ([key, value]) => key.endsWith("permission") && value
    );

    return !containsValidEmails || (failed && !werePermissionsTouched);
  };
  return (
    <Form<IFormValues>
      onSubmit={onSubmit}
      mutators={{ ...arrayMutators }}
      keepDirtyOnReinitialize
      initialValues={
        failedAgents.length
          ? {
              agents: failedAgents.map(({ email, permission }) => ({ email, permission })),
            }
          : {
              agents: [
                { email: null, permission: null },
                { email: null, permission: null },
                { email: null, permission: null },
              ],
            }
      }
      validate={formAgents => validate(agents, formAgents)}
    >
      {(props: FormRenderProps<IFormValues>) => (
        <StepScreen
          step={WelcomeTutorialStep.Invite}
          title={
            exceededBy
              ? `Update payment method to invite ${exceededBy} additional team ${plural(
                  exceededBy,
                  "member",
                  "members"
                )}`
              : "Invite teammates"
          }
          description={
            <>
              Bring all your teammates to HelpDesk and start giving excellent customer service together.
              <br />
              <br />
              Any accounts created during the trial are free!
            </>
          }
          nextButtonText={
            shouldShowSkip(containsValidEmails(props.values.agents), props.touched, failed)
              ? "Skip"
              : "Send invitations"
          }
          onNext={
            shouldShowSkip(containsValidEmails(props.values.agents), props.touched, failed) ? onSkip : props.form.submit
          }
          isNextLoading={isInviting || isSaving}
          onBack={onBack}
        >
          <InviteForm props={props} failed={failed} paymentError={error} />
        </StepScreen>
      )}
    </Form>
  );
};
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const validate = (agents: IAgent[], values: IFormValues) => {
  const errors: { agents?: { email?: string; permission?: string }[] } = {};
  errors.agents = values.agents.map(agent => {
    const agentErrors: { email?: string; permission?: string } = {};
    if (!agent.email) {
      return agentErrors;
    }
    if (!isEmail(agent.email)) {
      agentErrors.email = "Enter correct email address";
    }
    if (!agent.permission) {
      agentErrors.permission = "Select role";
    }
    if (agents.some(({ email }) => email === agent.email)) {
      agentErrors.email = "Agent is already registered in HelpDesk";
    }
    return agentErrors;
  });
  return errors;
};
